// THE catalog search rule — word-level matching over a SMALL catalog the
// caller already holds (the App Store, the Integrations shelf, the shell's
// "add an app" suggestions). Pure; no I/O, no element, no state.
//
// IT LIVES IN `shared/` BECAUSE IT HAS TWO CONSUMERS, NOT ONE (the same reason
// `storeItemText.js` lives here): the store itself
// (`packages/marketplace/functions/index.js` — its filter, its counts and its
// rank) and the SHELL (`packages/workspace/functions/search/searchAppResults.js`
// — the App Store and integration rows global search offers when nothing
// matched). Two copies of a search rule drift; one member typing one word must
// get one answer on both surfaces.
//
// It is also the rule the ASSISTANT's store search runs on the server
// (server `src/core/utils/catalogSearch.js`, 7c079e6c). The server and this
// workspace are separate repositories, so that file is ported here word for
// word — same limits, same stem, same levels, same rank — and
// `catalogSearch.test.js` pins the server's own cases so a drift fails here.
// Change the rule in both places or in neither.
//
// Why it exists: a substring match over each field ('schedule' is not a
// substring of 'Scheduling') told a member who typed "schedule" that the store
// had no such app, and a substring cannot rank.
//
// The rule: the query and every catalog text are split into WORDS; a query
// word matches a catalog word by
//   3  exact   the same word
//   2  prefix  the catalog word begins with the query word (3+ characters —
//              a 1–2 character query matches whole words only)
//   1  stem    both reduce to the same light English stem (schedule ↔
//              scheduling / schedules / scheduled / scheduler; book ↔
//              booking ↔ bookings)
// after one normalisation for both sides: lower-cased, accents folded, split
// on anything that is not a letter or a digit of any script.
//
// Script safety: nothing is ever UPPER-cased. Georgian has no case in its
// everyday script (Mkhedruli), and upper-casing it produces a different
// alphabet (Mtavruli) that matches nothing stored; lower-casing maps a
// Mtavruli query back onto Mkhedruli text. A non-Latin word is never stemmed:
// it matches by exact word or prefix.
//
// Safety: the query never becomes a regular expression — there is no pattern
// to inject and nothing to backtrack. Its length and its word count are capped.
//
// Several query words: an item must match EVERY word; when no item does, the
// items that match the most words answer instead (the caller already holds
// the whole catalog, so this narrows — it never widens what is visible).
// Rank: exact > prefix > stem, then the field's weight (a name beats a
// description), then the input order.
//
// `fields` — `[{ get(item) → string | string[], weight }]` — is the caller's:
// WHAT an item is searched on is a surface decision; HOW it matches is this
// file's, and only this file's.
//
// frank: every module-scope name below is globally distinct (`catalogSearch*`
// — frank hoists module-scope declarations into one globalScope), there is no
// module-scope `let`/`var` (`__tests__/frankModuleMutableGate.test.js`), and
// every regular expression is a literal INSIDE the function that uses it.

// Levels and limits — the server's numbers, unchanged.
const CATALOG_SEARCH_LEVEL = { EXACT: 3, PREFIX: 2, STEM: 1, NONE: 0 }
const CATALOG_SEARCH_LIMIT = {
  queryChars: 200,
  queryWords: 8,
  wordChars: 40,
  fieldChars: 4000,
  prefixChars: 3,
  stemChars: 3,
  // Not a server number: the bound on each memo table a caller's `cache` holds.
  cacheEntries: 20000
}
// One derivational ending is removed, the longest first. "Light" on purpose:
// the stem only has to be the SAME for the forms of one word, and a stem match
// ranks last — it is not a linguistic root.
const CATALOG_SEARCH_SUFFIXES = ['ment', 'ing', 'ion', 'ed', 'er']

/** Lower-cased, accent-folded text. Never upper-cases (see the header). */
export const catalogSearchNormalize = (value) => {
  if (value == null) return ''
  return String(value)
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
}

/** The words of a text: runs of letters / digits of any script. */
export const catalogSearchTokens = (value, opts) => {
  const max = (opts && opts.max) || Infinity
  const maxChars = (opts && opts.maxChars) || CATALOG_SEARCH_LIMIT.fieldChars
  const text = catalogSearchNormalize(String(value ?? '').slice(0, maxChars))
  const out = []
  for (const raw of text.split(/[^\p{L}\p{N}]+/u)) {
    if (!raw) continue
    out.push(
      raw.length > CATALOG_SEARCH_LIMIT.wordChars
        ? raw.slice(0, CATALOG_SEARCH_LIMIT.wordChars)
        : raw
    )
    if (out.length >= max) break
  }
  return out
}

/**
 * A light English stem of one normalised word. A word shorter than four
 * letters, or one that is not plain a–z (another script, digits), is returned
 * unchanged.
 */
export const catalogSearchStem = (token) => {
  let s = String(token ?? '')
  if (s.length < 4 || !/^[a-z]+$/.test(s)) return s
  // plural: companies → company, schedules → schedule, bookings → booking
  if (s.endsWith('ies') && s.length > 4) s = `${s.slice(0, -3)}y`
  else if (s.endsWith('s') && !s.endsWith('ss')) s = s.slice(0, -1)
  for (const suf of CATALOG_SEARCH_SUFFIXES) {
    if (s.endsWith(suf) && s.length - suf.length >= CATALOG_SEARCH_LIMIT.stemChars) {
      s = s.slice(0, -suf.length)
      break
    }
  }
  // planned → plann → plan (never ll / ss / zz: sell, pass, buzz keep theirs)
  const last = s[s.length - 1]
  if (
    s.length > CATALOG_SEARCH_LIMIT.stemChars &&
    last === s[s.length - 2] &&
    !'aeiou'.includes(last) &&
    !'lsz'.includes(last)
  ) {
    s = s.slice(0, -1)
  }
  // schedule → schedul, so it meets schedul(ing)
  if (s.length > CATALOG_SEARCH_LIMIT.stemChars && s.endsWith('e')) s = s.slice(0, -1)
  return s
}

/** How one normalised query word matches one normalised catalog word: 3 / 2 / 1 / 0. */
// @testOnly — exported for catalogSearch.test.js; no live code imports or calls it.
export const catalogSearchLevel = (queryToken, fieldToken) => {
  if (!queryToken || !fieldToken) return CATALOG_SEARCH_LEVEL.NONE
  if (queryToken === fieldToken) return CATALOG_SEARCH_LEVEL.EXACT
  if (queryToken.length >= CATALOG_SEARCH_LIMIT.prefixChars && fieldToken.startsWith(queryToken)) {
    return CATALOG_SEARCH_LEVEL.PREFIX
  }
  if (/^[a-z]+$/.test(queryToken) && /^[a-z]+$/.test(fieldToken)) {
    const qs = catalogSearchStem(queryToken)
    if (qs.length >= CATALOG_SEARCH_LIMIT.stemChars && qs === catalogSearchStem(fieldToken)) {
      return CATALOG_SEARCH_LEVEL.STEM
    }
  }
  return CATALOG_SEARCH_LEVEL.NONE
}

/** The query's words, capped (length and count). A symbols-only query has none. */
const catalogSearchQueryWords = (query) =>
  catalogSearchTokens(String(query ?? '').slice(0, CATALOG_SEARCH_LIMIT.queryChars), {
    max: CATALOG_SEARCH_LIMIT.queryWords,
    maxChars: CATALOG_SEARCH_LIMIT.queryChars
  })

// A caller that searches the same catalog again and again (a grid filters it
// row by row, on every keystroke) may pass `cache` — a plain `{}` it keeps
// between calls; these two tables fill it. They memoise PURE functions (a
// text's words, a word's stem), so a cache can only skip work — it can never
// change an answer, and a stale one cannot exist: a new text is a new key.
// Each table is emptied when it grows past `cacheEntries`.
const catalogSearchTextWords = (text, cache) => {
  if (!cache) return catalogSearchTokens(text)
  const table = cache.words || (cache.words = new Map())
  let out = table.get(text)
  if (out === undefined) {
    out = catalogSearchTokens(text)
    if (table.size >= CATALOG_SEARCH_LIMIT.cacheEntries) table.clear()
    table.set(text, out)
  }
  return out
}

// A word's light stem, or null for a word the stem never applies to (not
// plain a–z) — `catalogSearchLevel`'s stem test, computed once per word.
const catalogSearchWordStem = (word, cache) => {
  const table = cache && (cache.stems || (cache.stems = new Map()))
  if (table && table.has(word)) return table.get(word)
  const stem = /^[a-z]+$/.test(word) ? catalogSearchStem(word) : null
  if (table) {
    if (table.size >= CATALOG_SEARCH_LIMIT.cacheEntries) table.clear()
    table.set(word, stem)
  }
  return stem
}

// The query's words through the same cache: a grid asks once per ROW for one
// query, so the last query's words are kept.
const catalogSearchCachedQueryWords = (query, cache) => {
  if (!cache) return catalogSearchQueryWords(query)
  if (cache.query !== query || !cache.queryWords) {
    cache.queryWords = catalogSearchQueryWords(query)
    cache.query = query
  }
  return cache.queryWords
}

/**
 * The words of an item: every word of every field text, each with the weight
 * of the best field it sits in.
 */
const catalogSearchItemWords = (item, fields, cache) => {
  const itemWords = new Map()
  for (const f of fields || []) {
    const raw = f.get(item)
    const texts = Array.isArray(raw) ? raw : [raw]
    const weight = Number(f.weight) || 0
    for (const text of texts) {
      if (typeof text !== 'string' || !text) continue
      for (const w of catalogSearchTextWords(text, cache)) {
        if (!itemWords.has(w) || itemWords.get(w) < weight) itemWords.set(w, weight)
      }
    }
  }
  return itemWords
}

/**
 * How one item meets the query words: `{ matched, levelSum, weightSum }` —
 * how many query words it matches, the sum of their best levels, and the sum
 * of the weights of the fields those best matches sit in. The level of each
 * (query word, item word) pair is `catalogSearchLevel`'s, with the query
 * word's stem taken once per word instead of once per pair.
 */
export const catalogSearchScore = (item, words, fields, cache) => {
  const itemWords = catalogSearchItemWords(item, fields, cache)
  let matched = 0
  let levelSum = 0
  let weightSum = 0
  for (const qt of words || []) {
    const qStem = catalogSearchWordStem(qt, cache)
    let best = CATALOG_SEARCH_LEVEL.NONE
    let bestWeight = 0
    for (const [w, weight] of itemWords) {
      let level = CATALOG_SEARCH_LEVEL.NONE
      if (!qt || !w) level = CATALOG_SEARCH_LEVEL.NONE
      else if (qt === w) level = CATALOG_SEARCH_LEVEL.EXACT
      else if (qt.length >= CATALOG_SEARCH_LIMIT.prefixChars && w.startsWith(qt)) {
        level = CATALOG_SEARCH_LEVEL.PREFIX
      } else if (
        qStem !== null &&
        qStem.length >= CATALOG_SEARCH_LIMIT.stemChars &&
        qStem === catalogSearchWordStem(w, cache)
      ) {
        level = CATALOG_SEARCH_LEVEL.STEM
      }
      if (level > best || (level === best && level > 0 && weight > bestWeight)) {
        best = level
        bestWeight = weight
      }
    }
    if (best > CATALOG_SEARCH_LEVEL.NONE) {
      matched += 1
      levelSum += best
      weightSum += bestWeight
    }
  }
  return { matched, levelSum, weightSum }
}

/**
 * How many query words an item of `items` must match to be in the answer:
 * every word when at least one item has them all, else the most any item has
 * (0 when no item matches a word at all).
 */
export const catalogSearchThreshold = (items, words, fields, cache) => {
  const all = (words || []).length
  let best = 0
  for (const item of items || []) {
    const { matched } = catalogSearchScore(item, words, fields, cache)
    if (matched === all) return all
    if (matched > best) best = matched
  }
  return best
}

/**
 * Rank `items` against `query`: the matching items only, best first (see the
 * header for the rule). An empty / symbols-only query returns NO items — the
 * caller decides what an absent query means; this function never turns
 * "nothing to search for" into "everything". `opts`: `{ fields, cache? }`.
 */
export const catalogSearchRank = (items, query, opts) => {
  const fields = (opts && opts.fields) || []
  const cache = (opts && opts.cache) || null
  const words = catalogSearchCachedQueryWords(query, cache)
  const list = items ? Array.from(items) : []
  if (!words.length || !fields.length) return []
  const scored = []
  list.forEach((item, index) => {
    const s = catalogSearchScore(item, words, fields, cache)
    if (s.matched) scored.push({ item, index, ...s })
  })
  const all = scored.filter((s) => s.matched === words.length)
  const pool = all.length
    ? all
    : scored.filter((s) => s.matched === Math.max(...scored.map((x) => x.matched)))
  pool.sort((a, b) => b.levelSum - a.levelSum || b.weightSum - a.weightSum || a.index - b.index)
  return pool.map((s) => s.item)
}

/**
 * The per-item form of the same answer, for a caller that filters one item at
 * a time (a grid's `filter`, a count): is `item` among what
 * `catalogSearchRank(pool, query)` returns? `opts`: `{ fields, cache?,
 * poolThreshold }` — `poolThreshold(words)` must give
 * `catalogSearchThreshold(pool, words, fields)`; it is called ONLY for an item
 * that has some but not all of the words, so a caller may memoise it.
 */
export const catalogSearchIncludes = (item, query, opts) => {
  const fields = (opts && opts.fields) || []
  const cache = (opts && opts.cache) || null
  const words = catalogSearchCachedQueryWords(query, cache)
  if (!words.length || !fields.length) return false
  const { matched } = catalogSearchScore(item, words, fields, cache)
  if (!matched) return false
  if (matched === words.length) return true
  const poolThreshold = opts && opts.poolThreshold
  return typeof poolThreshold === 'function' && matched === poolThreshold(words)
}
